import React from 'react';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { 
  FileText, 
  Send, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Ban,
  HelpCircle,
  AlertTriangle,
  DollarSign,
  UserCheck,
  UserX,
  Pause,
  RefreshCw,
  CreditCard
} from 'lucide-react';
import { cn } from '@/lib/utils';

// =============================================================================
// TYPES
// =============================================================================

export type EntityType = 
  | 'estimate' 
  | 'invoice' 
  | 'credit' 
  | 'project' 
  | 'user' 
  | 'payment';

export type EstimateStatus = 'draft' | 'sent' | 'approved' | 'rejected' | 'expired' | 'cancelled';
export type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled' | 'partial';
export type CreditStatus = 'available' | 'partially_applied' | 'fully_applied' | 'cancelled' | 'issued' | 'exhausted';
export type ProjectStatus = 'planning' | 'pending_start' | 'in_progress' | 'on_hold' | 'delayed' | 'completed' | 'cancelled';
export type UserStatus = 'active' | 'suspended' | 'pending' | 'deactivated';
export type PaymentStatus = 'pending' | 'completed' | 'failed' | 'cancelled';

export type StatusValue = 
  | EstimateStatus 
  | InvoiceStatus 
  | CreditStatus 
  | ProjectStatus 
  | UserStatus 
  | PaymentStatus 
  | string;

interface StatusConfig {
  label: string;
  icon: React.ElementType;
  className: string;
  description: string;
  nextSteps?: string;
}

// =============================================================================
// STATUS CONFIGURATIONS
// =============================================================================

const ESTIMATE_STATUS_CONFIG: Record<EstimateStatus, StatusConfig> = {
  draft: {
    label: 'Draft',
    icon: FileText,
    className: 'bg-muted text-muted-foreground border-muted-foreground/20',
    description: 'This estimate is still being prepared and has not been sent to the customer.',
    nextSteps: 'Add items and finalize to send to customer.',
  },
  sent: {
    label: 'Sent',
    icon: Send,
    className: 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/30 dark:text-blue-300',
    description: 'This estimate has been sent to the customer and is awaiting their response.',
    nextSteps: 'Wait for customer approval or follow up with them.',
  },
  approved: {
    label: 'Approved',
    icon: CheckCircle,
    className: 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-300',
    description: 'The customer has approved this estimate. A project may be created and invoices can be issued.',
    nextSteps: 'Create invoices or make change orders if needed.',
  },
  rejected: {
    label: 'Rejected',
    icon: XCircle,
    className: 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-300',
    description: 'The customer has declined this estimate.',
    nextSteps: 'Create a revised version or archive this estimate.',
  },
  expired: {
    label: 'Expired',
    icon: Clock,
    className: 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300',
    description: 'This estimate has passed its validity period and is no longer active.',
    nextSteps: 'Create a new version with updated pricing and validity.',
  },
  cancelled: {
    label: 'Cancelled',
    icon: Ban,
    className: 'bg-gray-100 text-gray-600 border-gray-300 dark:bg-gray-800 dark:text-gray-400',
    description: 'This estimate has been cancelled and cannot be modified.',
    nextSteps: 'Create a new estimate if needed.',
  },
};

const INVOICE_STATUS_CONFIG: Record<InvoiceStatus, StatusConfig> = {
  draft: {
    label: 'Draft',
    icon: FileText,
    className: 'bg-muted text-muted-foreground border-muted-foreground/20',
    description: 'This invoice is still being prepared and has not been sent to the customer.',
    nextSteps: 'Finalize and send to customer.',
  },
  sent: {
    label: 'Sent',
    icon: Send,
    className: 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/30 dark:text-blue-300',
    description: 'This invoice has been sent to the customer and is awaiting payment.',
    nextSteps: 'Wait for payment or follow up with customer.',
  },
  paid: {
    label: 'Paid',
    icon: CheckCircle,
    className: 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-300',
    description: 'This invoice has been fully paid.',
    nextSteps: 'No action required. Invoice is complete.',
  },
  partial: {
    label: 'Partial',
    icon: Clock,
    className: 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300',
    description: 'This invoice has been partially paid.',
    nextSteps: 'Follow up for remaining balance.',
  },
  overdue: {
    label: 'Overdue',
    icon: AlertTriangle,
    className: 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-300',
    description: 'This invoice is past its due date and remains unpaid.',
    nextSteps: 'Send payment reminder or follow up urgently.',
  },
  cancelled: {
    label: 'Cancelled',
    icon: Ban,
    className: 'bg-gray-100 text-gray-600 border-gray-300 dark:bg-gray-800 dark:text-gray-400',
    description: 'This invoice has been cancelled and is no longer valid.',
    nextSteps: 'Create a new invoice if needed.',
  },
};

const CREDIT_STATUS_CONFIG: Record<CreditStatus, StatusConfig> = {
  available: {
    label: 'Available',
    icon: DollarSign,
    className: 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-300',
    description: 'This credit is available to apply to invoices.',
    nextSteps: 'Apply to outstanding invoices or issue a refund.',
  },
  issued: {
    label: 'Issued',
    icon: CreditCard,
    className: 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-300',
    description: 'This credit note has been issued and is available.',
    nextSteps: 'Apply to outstanding invoices or issue a refund.',
  },
  partially_applied: {
    label: 'Partially Applied',
    icon: Clock,
    className: 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300',
    description: 'Part of this credit has been applied to invoices.',
    nextSteps: 'Apply remaining balance or issue a refund.',
  },
  fully_applied: {
    label: 'Fully Applied',
    icon: CheckCircle,
    className: 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/30 dark:text-blue-300',
    description: 'This credit has been fully applied to invoices.',
    nextSteps: 'No action required.',
  },
  exhausted: {
    label: 'Exhausted',
    icon: CheckCircle,
    className: 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/30 dark:text-blue-300',
    description: 'This credit has been fully used.',
    nextSteps: 'No action required.',
  },
  cancelled: {
    label: 'Cancelled',
    icon: Ban,
    className: 'bg-gray-100 text-gray-600 border-gray-300 dark:bg-gray-800 dark:text-gray-400',
    description: 'This credit has been cancelled.',
    nextSteps: 'Create a new credit if needed.',
  },
};

const PROJECT_STATUS_CONFIG: Record<ProjectStatus, StatusConfig> = {
  planning: {
    label: 'Planning',
    icon: FileText,
    className: 'bg-muted text-muted-foreground border-muted-foreground/20',
    description: 'This project is in the planning phase.',
    nextSteps: 'Finalize scope and move to pending start.',
  },
  pending_start: {
    label: 'Pending Start',
    icon: Clock,
    className: 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300',
    description: 'This project is approved and waiting to begin.',
    nextSteps: 'Assign resources and start work.',
  },
  in_progress: {
    label: 'In Progress',
    icon: RefreshCw,
    className: 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-300',
    description: 'This project is currently in progress.',
    nextSteps: 'Continue work and track progress.',
  },
  on_hold: {
    label: 'On Hold',
    icon: Pause,
    className: 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300',
    description: 'This project is temporarily paused.',
    nextSteps: 'Resume when ready or cancel if needed.',
  },
  delayed: {
    label: 'Delayed',
    icon: AlertTriangle,
    className: 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-300',
    description: 'This project is behind schedule.',
    nextSteps: 'Address blockers and update timeline.',
  },
  completed: {
    label: 'Completed',
    icon: CheckCircle,
    className: 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/30 dark:text-blue-300',
    description: 'This project has been completed.',
    nextSteps: 'Send final invoice and close out.',
  },
  cancelled: {
    label: 'Cancelled',
    icon: Ban,
    className: 'bg-gray-100 text-gray-600 border-gray-300 dark:bg-gray-800 dark:text-gray-400',
    description: 'This project has been cancelled.',
    nextSteps: 'Review for lessons learned.',
  },
};

const USER_STATUS_CONFIG: Record<UserStatus, StatusConfig> = {
  active: {
    label: 'Active',
    icon: UserCheck,
    className: 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-300',
    description: 'This user account is active and can access the system.',
  },
  pending: {
    label: 'Pending',
    icon: Clock,
    className: 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300',
    description: 'This user account is pending activation.',
    nextSteps: 'Complete profile setup or verify email.',
  },
  suspended: {
    label: 'Suspended',
    icon: Pause,
    className: 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-300',
    description: 'This user account has been temporarily suspended.',
    nextSteps: 'Contact administrator to restore access.',
  },
  deactivated: {
    label: 'Deactivated',
    icon: UserX,
    className: 'bg-gray-100 text-gray-600 border-gray-300 dark:bg-gray-800 dark:text-gray-400',
    description: 'This user account has been deactivated.',
    nextSteps: 'Request reactivation if needed.',
  },
};

const PAYMENT_STATUS_CONFIG: Record<PaymentStatus, StatusConfig> = {
  pending: {
    label: 'Pending',
    icon: Clock,
    className: 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300',
    description: 'This payment is pending processing.',
  },
  completed: {
    label: 'Completed',
    icon: CheckCircle,
    className: 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-300',
    description: 'This payment has been completed successfully.',
  },
  failed: {
    label: 'Failed',
    icon: XCircle,
    className: 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-300',
    description: 'This payment failed to process.',
    nextSteps: 'Retry payment or use a different method.',
  },
  cancelled: {
    label: 'Cancelled',
    icon: Ban,
    className: 'bg-gray-100 text-gray-600 border-gray-300 dark:bg-gray-800 dark:text-gray-400',
    description: 'This payment has been cancelled.',
  },
};

// Default fallback for unknown statuses
const DEFAULT_STATUS_CONFIG: StatusConfig = {
  label: 'Unknown',
  icon: HelpCircle,
  className: 'bg-muted text-muted-foreground border-muted-foreground/20',
  description: 'Status information is not available.',
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function getStatusConfig(entityType: EntityType, status: string): StatusConfig {
  const normalizedStatus = status.toLowerCase();
  
  switch (entityType) {
    case 'estimate':
      return ESTIMATE_STATUS_CONFIG[normalizedStatus as EstimateStatus] || DEFAULT_STATUS_CONFIG;
    case 'invoice':
      return INVOICE_STATUS_CONFIG[normalizedStatus as InvoiceStatus] || DEFAULT_STATUS_CONFIG;
    case 'credit':
      return CREDIT_STATUS_CONFIG[normalizedStatus as CreditStatus] || DEFAULT_STATUS_CONFIG;
    case 'project':
      return PROJECT_STATUS_CONFIG[normalizedStatus as ProjectStatus] || DEFAULT_STATUS_CONFIG;
    case 'user':
      return USER_STATUS_CONFIG[normalizedStatus as UserStatus] || DEFAULT_STATUS_CONFIG;
    case 'payment':
      return PAYMENT_STATUS_CONFIG[normalizedStatus as PaymentStatus] || DEFAULT_STATUS_CONFIG;
    default:
      return DEFAULT_STATUS_CONFIG;
  }
}

// =============================================================================
// COMPONENT
// =============================================================================

interface UniversalStatusBadgeProps {
  /** The type of entity this status belongs to */
  entityType: EntityType;
  /** The status value */
  status: StatusValue;
  /** Optional date for automatic status derivation (e.g., due date for invoices, expires at for estimates) */
  dateContext?: string | null;
  /** Whether to show the interactive tooltip */
  showTooltip?: boolean;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Additional CSS classes */
  className?: string;
  /** Custom label override */
  labelOverride?: string;
}

/**
 * UniversalStatusBadge - Standardized status badge for all entity types
 * 
 * Features:
 * - Consistent styling across all entities (estimates, invoices, credits, projects, users)
 * - Interactive tooltips with status descriptions and next steps
 * - Automatic status derivation based on dates (e.g., overdue for past-due invoices)
 * - Size variants for different contexts
 * - Dark mode support
 * 
 * Usage:
 * ```tsx
 * <UniversalStatusBadge entityType="invoice" status="paid" />
 * <UniversalStatusBadge entityType="estimate" status="sent" dateContext={expiresAt} />
 * <UniversalStatusBadge entityType="credit" status="available" size="sm" showTooltip={false} />
 * ```
 */
const UniversalStatusBadge: React.FC<UniversalStatusBadgeProps> = ({
  entityType,
  status,
  dateContext,
  showTooltip = true,
  size = 'md',
  className,
  labelOverride,
}) => {
  // Derive effective status based on date context
  const getEffectiveStatus = (): string => {
    const normalizedStatus = status.toLowerCase();
    
    if (entityType === 'invoice' && normalizedStatus === 'sent' && dateContext) {
      const dueDate = new Date(dateContext);
      if (dueDate < new Date()) {
        return 'overdue';
      }
    }
    
    if (entityType === 'estimate' && normalizedStatus === 'sent' && dateContext) {
      const expiryDate = new Date(dateContext);
      if (expiryDate < new Date()) {
        return 'expired';
      }
    }
    
    return normalizedStatus;
  };

  const effectiveStatus = getEffectiveStatus();
  const config = getStatusConfig(entityType, effectiveStatus);
  const Icon = config.icon;

  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5 gap-1',
    md: 'text-sm px-2.5 py-0.5 gap-1.5',
    lg: 'text-sm px-3 py-1 gap-2',
  };

  const iconSizes = {
    sm: 'h-3 w-3',
    md: 'h-3.5 w-3.5',
    lg: 'h-4 w-4',
  };

  const badge = (
    <Badge
      variant="outline"
      className={cn(
        'inline-flex items-center font-medium border',
        config.className,
        sizeClasses[size],
        className
      )}
    >
      <Icon className={iconSizes[size]} />
      <span>{labelOverride || config.label}</span>
      {showTooltip && (
        <HelpCircle className={cn(iconSizes[size], 'opacity-50')} />
      )}
    </Badge>
  );

  if (!showTooltip) {
    return badge;
  }

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          {badge}
        </TooltipTrigger>
        <TooltipContent 
          side="bottom" 
          align="start"
          className="max-w-xs p-3"
        >
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Icon className="h-4 w-4" />
              <span className="font-semibold">{labelOverride || config.label}</span>
            </div>
            <p className="text-xs text-muted-foreground">
              {config.description}
            </p>
            {config.nextSteps && (
              <div className="pt-1 border-t">
                <p className="text-xs">
                  <span className="font-medium">Next: </span>
                  {config.nextSteps}
                </p>
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default UniversalStatusBadge;

// =============================================================================
// CONVENIENCE EXPORTS
// =============================================================================

export { getStatusConfig, ESTIMATE_STATUS_CONFIG, INVOICE_STATUS_CONFIG, CREDIT_STATUS_CONFIG, PROJECT_STATUS_CONFIG, USER_STATUS_CONFIG, PAYMENT_STATUS_CONFIG };
